import { useAuth } from '@clerk/clerk-react';
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';
import { useEffect, useRef } from 'react';

import { trpc } from '@/lib/api/trpc';

const TRACK_INTERVAL_MS = 24 * 60 * 60 * 1000;
// v2: bumped so existing devices re-send once with the ThumbmarkJS thumbmark.
const STORAGE_KEY = 'tgd_device_tracked_at_v2';

/**
 * ThumbmarkJS thumbmark — domain-agnostic: the same device produces the same
 * value here, in the main dashboard and on ATP storefronts. Options MUST stay
 * identical to src/lib/domain-atp/fingerprint-snippet.ts to keep values
 * matching across surfaces.
 */
async function getThumbmark(): Promise<string | undefined> {
  try {
    const tm = new Thumbmark({ logging: false, exclude: ['system.applePayVersion'] });
    const result = await tm.get();
    return result.thumbmark;
  } catch {
    return undefined;
  }
}

async function generateFingerprint(): Promise<string> {
  const signals: string[] = [];

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('TgdFP', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('TgdFP', 4, 17);
      signals.push(canvas.toDataURL());
    }
  } catch {
    // Canvas blocked
  }

  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        signals.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        signals.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch {
    // WebGL unavailable
  }

  signals.push(`${screen.width}x${screen.height}`);
  signals.push(`${screen.colorDepth}`);
  signals.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  signals.push(navigator.language);
  signals.push(String(navigator.hardwareConcurrency ?? ''));
  signals.push(String((navigator as any).deviceMemory ?? ''));
  signals.push(String(navigator.maxTouchPoints ?? 0));
  signals.push(navigator.platform ?? '');

  const raw = signals.join('|||');
  const data = new TextEncoder().encode(raw);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function getBrowserInfo(): { browser: string; os: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  if (ua.includes('Win')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return { browser, os };
}

/**
 * Invisible component that tracks the user's device fingerprint once per day.
 * Uses tRPC to call the backend, which extracts IP from headers server-side.
 */
export function UserDeviceTracker() {
  const { isSignedIn } = useAuth();
  const tracked = useRef(false);
  const registerDevice = trpc.userDeviceTracking.registerDevice.useMutation();

  useEffect(() => {
    if (!isSignedIn || tracked.current) return;

    const lastTracked = localStorage.getItem(STORAGE_KEY);
    if (lastTracked && Date.now() - Number(lastTracked) < TRACK_INTERVAL_MS) return;

    tracked.current = true;

    (async () => {
      try {
        const [fingerprint, thumbmark] = await Promise.all([
          generateFingerprint(),
          getThumbmark(),
        ]);
        const { browser, os } = getBrowserInfo();

        await registerDevice.mutateAsync({
          fingerprint,
          thumbmark,
          browser,
          os,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
        });

        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch (err) {
        console.error('[UserDeviceTracker] Failed:', err);
      }
    })();
  }, [isSignedIn]);

  return null;
}
